<script>
import { Line } from 'vue-chartjs'
import { CustomTooltips } from '@coreui/coreui-plugin-chartjs-custom-tooltips'
import { hexToRgba } from '@coreui/coreui-pro/dist/js/coreui-utilities'
import { minBy, maxBy } from 'lodash'
import { getDateFromStringWithoutTime } from '@/lib/date'
import { getUserPermissions } from '@/services/user-permissions'
import { userPermissionsMixin } from '@/mixins'
import Swal from 'sweetalert2'
import i18n from '@/i18n'

export default {
    name: 'CheckInsChart',
    mixins: [userPermissionsMixin],
    components: {
        hexToRgba,
        CustomTooltips,
    },
    props: {
        indicator: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            permissions: getUserPermissions(),
        }
    },
    extends: Line,
    mounted() {
        this.renderLineChart()
    },
    methods: {
        mapToChart(checkins) {
            if (this.yearToDate == 'accumulated') {
                return this.applyAccumulatedYTD(checkins).map((checkin) => {
                    return {
                        y: checkin.accumulatedCurrentValue,
                        x: this.getDateFromStringWithoutTime(checkin.date),
                    }
                })
            }

            return checkins.map((checkin) => {
                return {
                    y: checkin.current_value,
                    x: this.getDateFromStringWithoutTime(checkin.date),
                }
            })
        },
        applyAccumulatedYTD(checkIns) {
            if (checkIns.length) {
                return checkIns.map((checkIn, i, checkIns) => {
                    let acc = null
                    for (const [k, oldestCheckIn] of checkIns.entries()) {
                        if (i == k) {
                            acc += checkIn.current_value
                            checkIn.accumulatedCurrentValue = acc
                            return checkIn
                        }
                        acc += oldestCheckIn.current_value
                    }
                })
            }
            return []
        },
        openModaleditCheckIn(point, event) {
            if (!this.canEditCheckIn) {
                Swal({
                    type: 'warning',
                    title: i18n.t('notification.permissions.updateCheckInDenied'),
                })
            } else {
                const item = event[0]
                if (item) {
                    const checkIn = this.checkIns.accomplisheds[item._index]

                    if (!checkIn) return

                    if (this.indicator.type == 'goal') {
                        this.$router.push({
                            path: `/v2/target/targets/view/${checkIn.indicator_id}/checkin/${checkIn.id}`,
                        })
                    } else if (this.indicator.type == 'kpi') {
                        this.$router.push({
                            path: `/v2/target/indicators/view/${checkIn.indicator_id}/checkin/${checkIn.id}`,
                        })
                    }
                }
            }
        },
        // min and max date
        getTheSmallestDate(checkIns) {
            const smallestDate = minBy(checkIns, (checkIn) => {
                return this.getDateFromStringWithoutTime(checkIn.date)
            }).date

            return this.getDateFromStringWithoutTime(smallestDate)
        },
        getTheBiggestDate(checkIns) {
            const biggestDate = maxBy(checkIns, (checkIn) => {
                return this.getDateFromStringWithoutTime(checkIn.date)
            }).date

            return this.getDateFromStringWithoutTime(biggestDate)
        },
        getDateFromStringWithoutTime(string) {
            return getDateFromStringWithoutTime(string, 'YYYY-MM-DD')
        },
        renderLineChart() {
            this.renderChart(
                {
                    datasets: [
                        {
                            label: i18n.t('profile.table.details.expectancy'),
                            backgroundColor: hexToRgba('#dcdcdc', 60),
                            borderColor: hexToRgba('#dcdcdc', 80),
                            data: this.chartExpectations,
                        },
                        {
                            label: i18n.t('profile.table.details.progress'),
                            backgroundColor: hexToRgba('#20a8d8', 60),
                            borderColor: hexToRgba('#20a8d8', 80),
                            data: this.chartAccomplisheds,
                        },
                        {
                            label: i18n.t('form.label.forecast'),
                            backgroundColor: hexToRgba('#5588a3', 80),
                            borderColor: hexToRgba('#5588a3', 80),
                            data: this.chartSimulatedAccomplisheds,
                        },
                    ],
                },
                {
                    responsive: true,
                    maintainAspectRatio: false,
                    tooltips: {
                        enabled: false,
                        custom: CustomTooltips,
                        intersect: true,
                        mode: 'x',
                        position: 'nearest',
                        callbacks: {
                            labelColor: function (tooltipItem, chart) {
                                return {
                                    backgroundColor: chart.data.datasets[tooltipItem.datasetIndex].backgroundColor,
                                }
                            },
                        },
                    },
                    scales: {
                        yAxes: [
                            {
                                id: 'progress',
                                type: 'linear',
                                ticks: {
                                    min: this.minYAxeValue,
                                    max: this.maxYAxeValue,
                                },
                                display: true,
                                position: 'left',
                                beginAtZero: true,
                            },
                        ],
                        xAxes: [
                            {
                                id: 'time',
                                type: 'time',
                                distribution: 'linear',
                                time: {
                                    unit: 'month',
                                    displayFormats: {
                                        millisecond: 'MMM DD',
                                        second: 'MMM DD',
                                        minute: 'MMM DD',
                                        hour: 'MMM DD',
                                        day: 'MMM DD',
                                        week: 'MMM DD',
                                        month: 'MMM DD',
                                        quarter: 'MMM DD',
                                        year: 'MMM DD',
                                    },
                                    tooltipFormat: 'LL',
                                    min: this.getTheSmallestDate(this.checkIns.expectations),
                                    max: this.getTheBiggestDate(this.checkIns.expectations),
                                },
                            },
                        ],
                    },
                    onClick: this.openModaleditCheckIn,
                }
            )
        },
        reloadLineChart() {
            this.$data._chart.destroy()
            this.renderLineChart()
        },
    },
    computed: {
        chartExpectations() {
            return this.mapToChart(this.checkIns.expectations)
        },
        chartAccomplisheds() {
            return this.mapToChart(this.checkIns.accomplisheds)
        },
        chartSimulatedAccomplisheds() {
            return this.mapToChart(this.checkIns.simulatedAccomplisheds)
        },
        mappedExpectationsAndAccomplisheds() {
            if (this.checkIns.expectations.length || this.checkIns.accomplisheds.length) {
                if (this.yearToDate == 'accumulated') {
                    const expectationsYTD = this.applyAccumulatedYTD(this.checkIns.expectations)
                    const accomplishedsYTD = this.applyAccumulatedYTD(this.checkIns.accomplisheds)
                    const simulatedAccomplishedsYTD = this.applyAccumulatedYTD(this.checkIns.simulatedAccomplisheds)
                    return expectationsYTD
                        .concat(accomplishedsYTD)
                        .concat(simulatedAccomplishedsYTD)
                        .map((checkin) => checkin.accumulatedCurrentValue)
                        .filter((value) => value !== null)
                }

                return this.checkIns.expectations
                    .concat(this.checkIns.simulatedAccomplisheds)
                    .concat(this.checkIns.accomplisheds)
                    .map((checkin) => checkin.current_value)
                    .filter((value) => value !== null)
            }
            return []
        },
        maxYAxeValue() {
            return Math.max(...this.mappedExpectationsAndAccomplisheds, this.target, this.initialValue)
        },
        minYAxeValue() {
            return Math.min(...this.mappedExpectationsAndAccomplisheds, this.initialValue, this.target)
        },
        canEditCheckIn() {
            let editCheckinPermission = this.indicator.permissionsStage[this.targetLevel].edit_checkin
            if (this.userHasRolePermission) {
                editCheckinPermission = this.permissions.current_event_permissions.goals.personal.edit_checkin
            }
            return this.permissions && this.targetLevel ? editCheckinPermission : false
        },
        userHasRolePermission() {
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_target_master']
            const currentUser = this.$store.state.currentUser.user
            const rolesPermission = this.userHasPermission(currentUser, availableRoles, [])
            return rolesPermission
        },
        yearToDate() {
            return this.indicator.yearToDate.title
        },
        checkIns() {
            return this.indicator.kpiCheckins
        },
        target() {
            return this.indicator.target
        },
        initialValue() {
            return this.indicator.initialValue
        },
        targetLevel() {
            return this.indicator.level
        },
        polarity() {
            return this.indicator.polarity
        },
    },
    watch: {
        'indicator.formula'() {
            this.reloadLineChart()
        },
        'indicator.useProgressOnAutomaticCheckin'() {
            this.reloadLineChart()
        },
    },
}
</script>
